import React from "react";
import MUIAlert from "@mui/material/Alert";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import MUICard from "@mui/material/Card";
import MUICardContent from "@mui/material/CardContent";
import MUIChip from "@mui/material/Chip";
import MUIDivider from "@mui/material/Divider";
import MUILinearProgress from "@mui/material/LinearProgress";
import MUIStack from "@mui/material/Stack";
import MUIStep from "@mui/material/Step";
import MUIStepLabel from "@mui/material/StepLabel";
import MUIStepper from "@mui/material/Stepper";
import MUITable from "@mui/material/Table";
import MUITableBody from "@mui/material/TableBody";
import MUITableCell from "@mui/material/TableCell";
import MUITableContainer from "@mui/material/TableContainer";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUITextField from "@mui/material/TextField";
import MUITypography from "@mui/material/Typography";
import MUIOpenInNewIcon from "@mui/icons-material/OpenInNew";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

import Admin from "../Admin";
import PageHeader from "../../../Component/PageHeader";
import Security from "../../../Utility/Security";
import OrderStatus from "../../../Utility/Crud/OrderStatus";
import OrderStatusReason from "../../../Utility/Crud/OrderStatusReason";
import api from "../../../Seating/Security/api";
import NoteService from "../../../Seating/Security/NoteService/noteService";

const BATCH_SIZE = 5;
const STEPS = ["Find orders", "Review", "Choose route", "Route orders"];

export default class AdminMassRoute extends Admin {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      activeStep: 0,
      orderInput: "",
      orders: [],
      missingOrderNumbers: [],
      lookupComplete: 0,
      lookupTotal: 0,
      isLookingUp: false,
      selectedOrderStatusReason: null,
      noteText: "",
      routeComplete: 0,
      routeTotal: 0,
      isRouting: false,
    };
  }

  getMaxWidth() {
    return 1200;
  }

  renderHeader() {
    return (
      <PageHeader
        title="Mass Route"
        subtitle="Find orders and route them to a new order status reason, with an optional note."
      />
    );
  }

  parseOrderInput = () => {
    const entries = this.state.orderInput
      .split(/[\s,;]+/)
      .map((value) => value.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean);
    const orderNumbers = [];
    const invalidEntries = [];

    entries.forEach((entry) => {
      if (/^#?\d+$/.test(entry)) {
        orderNumbers.push(entry.replace(/^#/, "").replace(/^0+(?=\d)/, ""));
      } else {
        invalidEntries.push(entry);
      }
    });

    return {
      orderNumbers: [...new Set(orderNumbers)],
      invalidEntries: [...new Set(invalidEntries)],
    };
  };

  runInBatches = async (items, handler, onComplete) => {
    for (let index = 0; index < items.length; index += BATCH_SIZE) {
      const batch = items.slice(index, index + BATCH_SIZE);
      await Promise.all(
        batch.map(async (item) => {
          const result = await handler(item);
          onComplete(result);
        }),
      );
    }
  };

  lookupOrder = async (orderNumber) => {
    try {
      const response = await api.send("POST", "/search/omniSearch", {
        omniSearchTerm: orderNumber,
      });
      const searchOrder = (response.orders || []).find(
        (order) => String(order.id) === orderNumber,
      );

      if (!searchOrder) {
        return { orderNumber, missing: true };
      }

      return {
        ...searchOrder,
        requestedOrderNumber: orderNumber,
        resultStatus: "ready",
      };
    } catch (error) {
      return { orderNumber, missing: true, error };
    }
  };

  handleLookup = async () => {
    const { orderNumbers, invalidEntries } = this.parseOrderInput();
    if (orderNumbers.length === 0 && invalidEntries.length === 0) {
      NotistackEnqueueSnackbar("Enter at least one value.", {
        variant: "warning",
      });
      return;
    }

    this.setState({
      activeStep: 1,
      orders: [],
      missingOrderNumbers: invalidEntries,
      lookupComplete: invalidEntries.length,
      lookupTotal: orderNumbers.length + invalidEntries.length,
      isLookingUp: orderNumbers.length > 0,
    });

    const orders = [];
    const missingOrderNumbers = [...invalidEntries];

    await this.runInBatches(orderNumbers, this.lookupOrder, (result) => {
      if (result.missing) {
        missingOrderNumbers.push(result.orderNumber);
      } else {
        orders.push(result);
      }
      this.setState((state) => ({ lookupComplete: state.lookupComplete + 1 }));
    });

    orders.sort((a, b) => Number(a.id) - Number(b.id));
    this.setState({
      orders,
      missingOrderNumbers,
      isLookingUp: false,
    });
  };

  getPatientName = (order) => {
    if (order.patientName) {
      return order.patientName;
    }
    if (order.patient) {
      return [order.patient.firstName, order.patient.lastName]
        .filter(Boolean)
        .join(" ");
    }
    return "";
  };

  getOrderStatusReason = (order) =>
    this.context.orderStatusReasonsIndexed[order.orderStatusReasonId] || null;

  getOrderStatusName = (order) => {
    const reason = this.getOrderStatusReason(order);
    const statusId = reason ? reason.orderStatus : order.status;
    return (
      OrderStatus.getDisplayName(this.context.orderStatusesIndexed[statusId]) ||
      (typeof order.status === "string" ? order.status : "")
    );
  };

  getOrderStatusReasonName = (order) =>
    OrderStatusReason.getDisplayName(this.getOrderStatusReason(order)) ||
    "None";

  getOrderStatusReasonOptions = () =>
    Object.values(this.context.orderStatusReasonsIndexed)
      .filter((orderStatusReason) => !orderStatusReason.deleted)
      .sort((a, b) => {
        if (a.orderStatus !== b.orderStatus) {
          return a.orderStatus - b.orderStatus;
        }
        return (a.description || "").localeCompare(b.description || "");
      });

  getOrderStatusReasonOptionLabel = (orderStatusReason) => {
    const status =
      this.context.orderStatusesIndexed[orderStatusReason.orderStatus];
    return `${OrderStatus.getDisplayName(status)} • ${OrderStatusReason.getDisplayName(orderStatusReason)}`;
  };

  openOrder = (order) => {
    window.open(
      `/chart/${order.patientId}/order/${order.id}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  createNote = (order) => {
    const noteText = this.state.noteText.trim();
    if (!noteText) {
      return Promise.resolve();
    }

    return NoteService.createActivityNote(order.id, {
      text: noteText,
      accountId: order.activity.accountId,
      completed: true,
      task: false,
      createdAt: new Date().toISOString(),
      activityId: order.activity.id,
      createdById: this.context.currentUser.id,
    });
  };

  routeOrder = async (order) => {
    const destination = this.state.selectedOrderStatusReason;
    if (order.orderStatusReasonId === destination.id) {
      return { id: order.id, resultStatus: "skipped" };
    }

    try {
      await api.send(
        "POST",
        "/chart/update",
        {
          patientId: Number(order.patientId),
          updateData: {
            orderActivity: {
              [order.id]: {
                id: order.id,
                status: destination.orderStatus,
                orderStatusReasonId: destination.id,
                lastUpdatedOn: order.lastUpdatedOn,
              },
            },
          },
          returnChart: false,
        },
        0,
      );
    } catch (error) {
      return { id: order.id, resultStatus: "failed", error };
    }

    try {
      await this.createNote(order);
      return {
        id: order.id,
        status: destination.orderStatus,
        orderStatusReasonId: destination.id,
        resultStatus: "routed",
      };
    } catch (error) {
      return {
        id: order.id,
        status: destination.orderStatus,
        orderStatusReasonId: destination.id,
        resultStatus: "noteFailed",
        error,
      };
    }
  };

  handleRoute = async () => {
    if (!this.state.selectedOrderStatusReason) {
      NotistackEnqueueSnackbar("Choose an order status reason.", {
        variant: "warning",
      });
      return;
    }

    this.setState({
      activeStep: 3,
      routeComplete: 0,
      routeTotal: this.state.orders.length,
      isRouting: true,
    });

    await this.runInBatches(this.state.orders, this.routeOrder, (result) => {
      this.setState((state) => ({
        orders: state.orders.map((order) =>
          order.id === result.id ? { ...order, ...result } : order,
        ),
        routeComplete: state.routeComplete + 1,
      }));
    });

    this.setState({ isRouting: false });
  };

  handleStartOver = () => {
    this.setState({
      activeStep: 0,
      orderInput: "",
      orders: [],
      missingOrderNumbers: [],
      lookupComplete: 0,
      lookupTotal: 0,
      isLookingUp: false,
      selectedOrderStatusReason: null,
      noteText: "",
      routeComplete: 0,
      routeTotal: 0,
      isRouting: false,
    });
  };

  renderProgress(label, complete, total) {
    const value = total === 0 ? 0 : (complete / total) * 100;
    return (
      <MUIBox>
        <MUIStack
          direction="row"
          sx={{
            justifyContent: "space-between",
            mb: 0.75,
          }}
        >
          <MUITypography variant="body2">{label}</MUITypography>
          <MUITypography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {complete} of {total}
          </MUITypography>
        </MUIStack>
        <MUILinearProgress variant="determinate" value={value} />
      </MUIBox>
    );
  }

  renderOrdersTable(showResult = false) {
    return (
      <MUITableContainer>
        <MUITable size="small">
          <MUITableHead>
            <MUITableRow>
              <MUITableCell>Order #</MUITableCell>
              <MUITableCell>Patient Name</MUITableCell>
              <MUITableCell>Order Status</MUITableCell>
              <MUITableCell>Order Status Reason</MUITableCell>
              {showResult && <MUITableCell>Result</MUITableCell>}
              <MUITableCell align="right">Order</MUITableCell>
            </MUITableRow>
          </MUITableHead>
          <MUITableBody>
            {this.state.orders.map((order) => (
              <MUITableRow key={order.id}>
                <MUITableCell>{order.id}</MUITableCell>
                <MUITableCell>{this.getPatientName(order)}</MUITableCell>
                <MUITableCell>{this.getOrderStatusName(order)}</MUITableCell>
                <MUITableCell>
                  {this.getOrderStatusReasonName(order)}
                </MUITableCell>
                {showResult && (
                  <MUITableCell>
                    {order.resultStatus === "routed" && (
                      <MUIChip size="small" color="success" label="Routed" />
                    )}
                    {order.resultStatus === "skipped" && (
                      <MUIChip size="small" label="Skipped" />
                    )}
                    {order.resultStatus === "failed" && (
                      <MUIChip size="small" color="error" label="Failed" />
                    )}
                    {order.resultStatus === "noteFailed" && (
                      <MUIChip
                        size="small"
                        color="warning"
                        label="Routed; note failed"
                      />
                    )}
                    {order.resultStatus === "ready" && (
                      <MUIChip
                        size="small"
                        variant="outlined"
                        label="Waiting"
                      />
                    )}
                  </MUITableCell>
                )}
                <MUITableCell align="right">
                  <MUIButton
                    size="small"
                    endIcon={<MUIOpenInNewIcon />}
                    onClick={() => this.openOrder(order)}
                  >
                    Open
                  </MUIButton>
                </MUITableCell>
              </MUITableRow>
            ))}
          </MUITableBody>
        </MUITable>
      </MUITableContainer>
    );
  }

  renderFindOrders() {
    return (
      <MUIStack spacing={3}>
        <MUITextField
          label="Order numbers"
          placeholder={
            "Paste CSV, whitespace-separated, or one order number per line"
          }
          value={this.state.orderInput}
          onChange={(event) =>
            this.setState({
              orderInput: event.target.value,
              orders: [],
              missingOrderNumbers: [],
              lookupComplete: 0,
              lookupTotal: 0,
            })
          }
          multiline
          minRows={6}
          fullWidth
          disabled={this.state.isLookingUp}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
        <MUIStack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <MUIButton
            variant="contained"
            disabled={
              this.state.isLookingUp ||
              this.state.orderInput.trim().length === 0
            }
            onClick={this.handleLookup}
          >
            Next
          </MUIButton>
        </MUIStack>
      </MUIStack>
    );
  }

  renderReview() {
    const hasResults = this.state.orders.length > 0;

    return (
      <MUIStack spacing={3}>
        {this.renderProgress(
          this.state.isLookingUp ? "Looking up orders…" : "Lookup complete",
          this.state.lookupComplete,
          this.state.lookupTotal,
        )}
        {!this.state.isLookingUp &&
          this.state.missingOrderNumbers.length > 0 && (
            <MUIAlert severity="warning">
              Not found: {this.state.missingOrderNumbers.join(", ")}
            </MUIAlert>
          )}
        {!this.state.isLookingUp && hasResults && this.renderOrdersTable()}
        {!this.state.isLookingUp && !hasResults && (
          <MUIAlert severity="warning">No orders were found.</MUIAlert>
        )}
        <MUIStack
          direction="row"
          sx={{
            justifyContent: "space-between",
          }}
        >
          <MUIButton
            disabled={this.state.isLookingUp}
            onClick={() => this.setState({ activeStep: 0 })}
          >
            Back
          </MUIButton>
          <MUIButton
            variant="contained"
            disabled={this.state.isLookingUp || !hasResults}
            onClick={() => this.setState({ activeStep: 2 })}
          >
            Next
          </MUIButton>
        </MUIStack>
      </MUIStack>
    );
  }

  renderChooseRoute() {
    const destination = this.state.selectedOrderStatusReason;
    const skippedCount = destination
      ? this.state.orders.filter(
          (order) => order.orderStatusReasonId === destination.id,
        ).length
      : 0;

    return (
      <MUIStack spacing={3}>
        <MUIAutocomplete
          options={this.getOrderStatusReasonOptions()}
          value={destination}
          onChange={(event, value) =>
            this.setState({ selectedOrderStatusReason: value })
          }
          getOptionLabel={this.getOrderStatusReasonOptionLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <MUITextField
              {...params}
              label="Destination"
              variant="standard"
              size="small"
              fullWidth
              required
            />
          )}
        />
        <MUITextField
          label="Note"
          placeholder="Optional note to add to all orders after routing."
          value={this.state.noteText}
          onChange={(event) => this.setState({ noteText: event.target.value })}
          multiline
          minRows={6}
          fullWidth
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
        {destination && skippedCount > 0 && (
          <MUIAlert severity="info">
            {skippedCount} {skippedCount === 1 ? "order is" : "orders are"}{" "}
            already in this order status reason and will be skipped.
          </MUIAlert>
        )}
        <MUIDivider />
        <MUITypography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {this.state.orders.length} orders selected.
        </MUITypography>
        <MUIStack
          direction="row"
          sx={{
            justifyContent: "space-between",
          }}
        >
          <MUIButton onClick={() => this.setState({ activeStep: 1 })}>
            Back
          </MUIButton>
          <MUIButton
            variant="contained"
            disabled={!destination}
            onClick={this.handleRoute}
          >
            Route orders
          </MUIButton>
        </MUIStack>
      </MUIStack>
    );
  }

  renderRouteProgress() {
    const routedCount = this.state.orders.filter(
      (order) => order.resultStatus === "routed",
    ).length;
    const skippedCount = this.state.orders.filter(
      (order) => order.resultStatus === "skipped",
    ).length;
    const failedCount = this.state.orders.filter(
      (order) =>
        order.resultStatus === "failed" || order.resultStatus === "noteFailed",
    ).length;

    return (
      <MUIStack spacing={3}>
        {this.renderProgress(
          this.state.isRouting ? "Routing orders…" : "Routing complete",
          this.state.routeComplete,
          this.state.routeTotal,
        )}
        {!this.state.isRouting && (
          <MUIAlert severity={failedCount > 0 ? "warning" : "success"}>
            Routed {routedCount}, skipped {skippedCount}, failed {failedCount}.
          </MUIAlert>
        )}
        {this.renderOrdersTable(true)}
        {!this.state.isRouting && (
          <MUIBox sx={{ display: "flex", justifyContent: "flex-end" }}>
            <MUIButton
              type="button"
              variant="contained"
              onClick={this.handleStartOver}
            >
              Route more orders
            </MUIButton>
          </MUIBox>
        )}
      </MUIStack>
    );
  }

  renderContent() {
    if (!Security.hasPermission("module.adminMassRoute")) {
      return (
        <MUIAlert severity="error">
          You do not have access to Mass Route.
        </MUIAlert>
      );
    }

    return (
      <MUICard>
        <MUICardContent>
          <MUIStepper activeStep={this.state.activeStep} sx={{ mb: 4 }}>
            {STEPS.map((step) => (
              <MUIStep key={step}>
                <MUIStepLabel>{step}</MUIStepLabel>
              </MUIStep>
            ))}
          </MUIStepper>
          {this.state.activeStep === 0 && this.renderFindOrders()}
          {this.state.activeStep === 1 && this.renderReview()}
          {this.state.activeStep === 2 && this.renderChooseRoute()}
          {this.state.activeStep === 3 && this.renderRouteProgress()}
        </MUICardContent>
      </MUICard>
    );
  }
}
